import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useScroll } from "framer-motion";
import { ArrowLeft, Clock, Link2, MessageCircle, Twitter, Heart } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate, readTime, bookmarks } from "../lib/api";
import ContentBlocks from "../components/ContentBlocks";
import SEO from "../components/SEO";
import { Reveal } from "../components/Reveal";
import { Badge } from "../components/Pill";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [saved, setSaved] = useState(false);
  const { scrollYProgress } = useScroll();

  useEffect(() => {
    setPost(null);
    setRelated([]);
    api(`/blogs/${slug}`)
      .then((p) => {
        setPost(p);
        setSaved(bookmarks.has(slug));
        api(`/blogs?category=${encodeURIComponent(p.category)}&limit=4`)
          .then((r) => setRelated(r.filter((x) => x.slug !== slug).slice(0, 3)))
          .catch(() => {});
      })
      .catch(() => setPost(false));
  }, [slug]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied. Share the love.");
    } catch {
      toast.error("Could not copy the link");
    }
  };

  const toggleSave = () => {
    const next = bookmarks.toggle(slug);
    setSaved(next.includes(slug));
    toast.success(next.includes(slug) ? "Saved to your reading list" : "Removed from your reading list");
  };

  if (post === false) {
    return (
      <main className="min-h-screen bg-cream pt-40 px-6 text-center" data-testid="blog-not-found">
        <h1 className="font-display uppercase text-4xl text-ink">This story has moved.</h1>
        <Link to="/edit" className="mt-6 inline-block text-xs font-bold uppercase tracking-[0.25em] text-gold">
          Back to The Edit
        </Link>
      </main>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <p className="font-elegant italic text-2xl text-gold animate-pulse">Opening the story</p>
      </div>
    );
  }

  const url = typeof window !== "undefined" ? window.location.href : "";
  const wa = `https://wa.me/?text=${encodeURIComponent(post.title + " " + url)}`;
  const x = `https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(url)}`;

  return (
    <main data-testid="blog-post-page" className="bg-cream">
      <SEO
        title={post.title}
        description={post.excerpt}
        image={post.cover_image}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title,
          description: post.excerpt,
          image: post.cover_image,
          author: { "@type": "Person", name: post.author },
          publisher: { "@type": "Organization", name: "The Brides of India" },
          datePublished: post.created_at,
          dateModified: post.updated_at || post.created_at,
        }}
      />
      <motion.div
        data-testid="reading-progress"
        className="fixed top-0 left-0 right-0 h-1 bg-champagne z-[70] origin-left"
        style={{ scaleX: scrollYProgress }}
      />

      <article className="pt-32 md:pt-40 pb-20">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <Badge tone="ink">{post.category}</Badge>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-blush px-3.5 py-1.5 text-[9px] font-bold uppercase tracking-[0.18em] text-ink">
                <Clock size={11} /> {readTime(post.content)} min read
              </span>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 data-testid="blog-title" className="mt-7 font-display uppercase text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-ink leading-[1.02]">
              {post.title}
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p data-testid="blog-byline" className="mt-6 text-[10px] font-bold uppercase tracking-[0.25em] text-ink/50">
              Words by {post.author} · {fmtDate(post.created_at)}
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <div className="mt-8 flex items-center justify-center gap-3 flex-wrap" data-testid="share-row">
              <button onClick={copyLink} data-testid="share-copy-button"
                className="inline-flex items-center gap-2 rounded-full border-2 border-ink/15 px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.18em] text-ink/70 hover:border-gold hover:text-gold transition-colors duration-300">
                <Link2 size={13} /> Copy Link
              </button>
              <a href={wa} target="_blank" rel="noreferrer" data-testid="share-whatsapp-button"
                className="inline-flex items-center gap-2 rounded-full border-2 border-ink/15 px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.18em] text-ink/70 hover:border-gold hover:text-gold transition-colors duration-300">
                <MessageCircle size={13} /> WhatsApp
              </a>
              <a href={x} target="_blank" rel="noreferrer" data-testid="share-x-button"
                className="inline-flex items-center gap-2 rounded-full border-2 border-ink/15 px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.18em] text-ink/70 hover:border-gold hover:text-gold transition-colors duration-300">
                <Twitter size={13} /> Post
              </a>
              <button onClick={toggleSave} data-testid="article-save-button"
                className={`inline-flex items-center gap-2 rounded-full border-2 px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.18em] transition-colors duration-300 ${
                  saved ? "bg-rose border-rose text-ink" : "border-ink/15 text-ink/70 hover:border-rose hover:text-rose"
                }`}>
                <Heart size={13} fill={saved ? "currentColor" : "none"} /> {saved ? "Saved" : "Save"}
              </button>
            </div>
          </Reveal>
        </div>

        {post.cover_image && (
          <Reveal delay={0.15} className="max-w-5xl mx-auto px-6 mt-12">
            <div className="overflow-hidden rounded-[2rem]">
              <img src={post.cover_image} alt={post.title} data-testid="blog-cover" className="w-full aspect-[16/9] object-cover" />
            </div>
          </Reveal>
        )}

        <div className="max-w-2xl mx-auto px-6 mt-14">
          <p className="font-elegant italic text-2xl md:text-[1.7rem] leading-relaxed text-ink/80 mb-10">{post.excerpt}</p>
          <ContentBlocks content={post.content} />
          <div className="mt-16 rounded-[2rem] bg-blush p-8 flex items-center justify-between gap-6 flex-wrap">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold">Filed under {post.category}</p>
              <p className="mt-2 font-elegant italic text-xl text-ink/70">The Brides of India Edit</p>
            </div>
            <Link
              to="/edit"
              data-testid="blog-back-link"
              className="group inline-flex items-center gap-2 rounded-full bg-ink text-cream px-6 py-3 text-[10px] font-bold uppercase tracking-[0.2em] hover:bg-gold hover:text-ink transition-colors duration-300"
            >
              <ArrowLeft size={14} className="transition-transform duration-300 group-hover:-translate-x-1" />
              More Stories
            </Link>
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="bg-blush py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <Reveal><Badge tone="ink">Keep Reading</Badge></Reveal>
            <div className="grid md:grid-cols-3 gap-8 mt-10">
              {related.map((b, i) => (
                <Reveal key={b.id} delay={0.06 * i}>
                  <Link to={`/edit/${b.slug}`} data-testid={`related-post-${b.slug}`} className="group block">
                    <div className="overflow-hidden rounded-[1.75rem]">
                      <img
                        src={b.cover_image}
                        alt={b.title}
                        loading="lazy"
                        className="w-full aspect-[4/3] object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                      />
                    </div>
                    <div className="mt-4"><Badge tone="white">{b.category}</Badge></div>
                    <h3 className="mt-3 font-display uppercase text-xl md:text-2xl text-ink leading-tight group-hover:text-gold transition-colors duration-300">
                      {b.title}
                    </h3>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
